/**
 * V2HeaderBarSection — V2-native editor section for step.content.header
 *
 * Edits: Back button, Skip button, Progress Indicator (with nested Logo & Layout),
 *        Header Settings (height, bg, opacity, insets)
 *
 * V2 paths: content.header.back/skip/close, content.header.progressIndicator
 *
 * All progress-related settings (indicator kind, line/dashes appearance,
 * image/logo, layout) live inside a flat ProgressIndicator object.
 * Logo and Progress Layout are sub-sections inside V2PageIndicatorElement's gear Collapse.
 */

import React, { useCallback, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Switch,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { SAccordion } from '@modules/ui-components';
import { TextElement } from '../../../factories/elements/TextElement';
import { V2PageIndicatorElement, type V2PageIndicatorKindType } from '../../../factories/elements/V2PageIndicatorElement';
import { PaddingElement, type PaddingValues } from '../../../factories/elements';
import { OnboardingMediaUploadService } from '@modules/firebase-core/services/OnboardingMediaUploadService';
import { StyleFieldRow } from '../../shared/styleFields/StyleFieldRow';
import { ButtonStyleSections } from '../../shared/styleFields/ButtonStyleSections';
import { EditorGuideProvider } from '../../../contexts';
import {
  V2_BACK_BUTTON_CONFIG,
  V2_SKIP_BUTTON_CONFIG,
  V2_PROGRESS_INDICATOR_CONFIG,
  V2_HEADER_HEIGHT_CONFIG,
  V2_HEADER_BG_COLOR_CONFIG,
  V2_HEADER_OPACITY_CONFIG,
  V2_HEADER_INSETS_PATH,
  V2_PROGRESS_ICON_FIELDS,
  V2_PROGRESS_ICON_STYLE_GROUP,
  V2_PROGRESS_CONTENT_ALIGNMENT_CONFIG,
  V2_PROGRESS_FULL_WIDTH_CONFIG,
  V2_PROGRESS_RESPECT_BUTTON_CONFIG,
  V2_PROGRESS_V_ALIGN_CONFIG,
  V2_PROGRESS_INSETS_PATH,
} from '../../../config/v2HeaderBarConfig';
import { SECTION_MARGIN_BOTTOM, FIELD_MARGIN_BOTTOM, FIELD_STYLE_GRID_GAP, FIELD_STYLE_MARGIN_TOP } from '../../../config/ui';
import {
  expandedSectionSx,
  expandedToggleButtonSx,
  expandedToggleButtonActiveSx,
  mainFieldLabelEnabledSx,
} from '../../../config/editorFieldStyles';
import { getValueByPath, isFieldEnabled, setNestedValue } from '../../../utils/v2FieldOperations';
import { getV2TranslationValue } from '../../../utils/v2FieldOperations';
import {
  getStyleValues as getStyleValuesUtil,
} from '../../../utils/fieldValueOperations';
import { useEditorPresentationProfile } from './PresentationProfileContext';
import {
  getSectionTestId,
  getFieldTestId,
  getCheckboxTestId,
  getStylesToggleTestId,
  getStyleGroupTestId,
  getInputTestId,
  getSliderTestId,
} from '../../../utils/testIdUtils';
import type { FieldConfig } from '../../../types/editorFieldTypes';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import {
  getThemedSkipButtonDefaults,
  getThemedBackButtonDefaults,
  getThemedCloseButtonDefaults,
  getThemedProgressIndicatorDefaults,
  getFactorySkipButtonDefaults,
  getFactoryBackButtonDefaults,
  getFactoryCloseButtonDefaults,
  getFactoryProgressIndicatorDefaults,
} from '../../../utils/v2ThemeDefaults';

// ── Padding path translation (PaddingElement → EdgeInsets) ──────────

function translatePaddingPath(path: string): string {
  return path
    .replace('.paddingTop', '.top')
    .replace('.paddingRight', '.end')
    .replace('.paddingBottom', '.bottom')
    .replace('.paddingLeft', '.start');
}

function getEdgeInsetsAsPaddingValues(step: FlowStepNode, basePath: string): PaddingValues {
  const top = getValueByPath<number>(step, `${basePath}.top`);
  const end = getValueByPath<number>(step, `${basePath}.end`);
  const bottom = getValueByPath<number>(step, `${basePath}.bottom`);
  const start = getValueByPath<number>(step, `${basePath}.start`);
  return {
    paddingTop: typeof top === 'number' ? top : 0,
    paddingRight: typeof end === 'number' ? end : 0,
    paddingBottom: typeof bottom === 'number' ? bottom : 0,
    paddingLeft: typeof start === 'number' ? start : 0,
  };
}

export interface V2HeaderBarSectionProps {
  /** V2 StepNode being edited */
  step: FlowStepNode;

  /** Current editing locale */
  locale: string;

  /** Callback when a V2 path value changes */
  onChange: (path: string, value: unknown) => void;

  /** Whether section is disabled (saving state) */
  disabled?: boolean;

  /** Whether section is expanded by default */
  defaultExpanded?: boolean;

  /** Override accordion title (used by FlexibleLayout for section labels) */
  sectionTitle?: string;

  /** Firebase context for image upload */
  projectId?: string;
  onboardingId?: string;
  versionId?: string;

  /** Theme settings for themed defaults on element toggle ON */
  themeSettingsV2?: OnboardingThemeSettingsV2 | null;
}

export const V2HeaderBarSection: React.FC<V2HeaderBarSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  sectionTitle,
  projectId,
  onboardingId,
  versionId,
  themeSettingsV2,
}) => {
  // Track expanded style panels
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const [headerSettingsExpanded, setHeaderSettingsExpanded] = useState(false);

  const toggleFieldExpansion = useCallback((path: string) => {
    setExpandedFields(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  // Get text value — V2 uses 'translations' not 'l10n'
  const getFieldValue = useCallback(
    (config: FieldConfig): string => {
      if (config.l10nPath) {
        return getV2TranslationValue(step, config.l10nPath, locale);
      }
      return getValueByPath<string>(step, config.path) ?? '';
    },
    [step, locale]
  );

  // Check if field is enabled (not null)
  const checkFieldEnabled = useCallback(
    (config: FieldConfig): boolean => isFieldEnabled(step, config.path),
    [step]
  );

  const activeProfile = useEditorPresentationProfile();

  // Get style values — reuse V1 utility (works with any object + config)
  const getStyleValues = useCallback(
    (config: FieldConfig): Record<string, unknown> => getStyleValuesUtil(step as unknown as Record<string, unknown>, config, activeProfile),
    [step, activeProfile]
  );

  // Handle text change — write to V2 translations path
  const handleFieldChange = useCallback(
    (config: FieldConfig, value: string) => {
      if (config.l10nPath) {
        onChange(`${config.l10nPath}.translations.${locale}`, value);
      } else {
        onChange(config.path, value);
      }
    },
    [onChange, locale]
  );

  // Handle enable/disable toggle — uses themed defaults when available
  const handleEnabledChange = useCallback(
    (config: FieldConfig, enabled: boolean) => {
      if (enabled) {
        if (config.type === 'pageIndicator') {
          // Initialize progress indicator with themed activeColor
          if (themeSettingsV2) {
            onChange(config.path, getThemedProgressIndicatorDefaults(themeSettingsV2, (config.defaultValue as number) ?? 50));
          } else {
            onChange(config.path, getFactoryProgressIndicatorDefaults((config.defaultValue as number) ?? 50));
          }
        } else if (config.l10nPath) {
          // For V2 text fields with l10nPath (e.g. skip/back TapTarget buttons)
          const defaultText = typeof config.defaultValue === 'string' ? config.defaultValue : '';
          const isSkip = config.path.includes('skip');
          const isBack = config.path.includes('back');
          if (themeSettingsV2) {
            if (isSkip) {
              onChange(config.path, getThemedSkipButtonDefaults(themeSettingsV2, locale, defaultText || 'SKIP'));
            } else if (isBack) {
              onChange(config.path, getThemedBackButtonDefaults(themeSettingsV2, locale, defaultText || '❮'));
            } else {
              onChange(config.path, getThemedCloseButtonDefaults(themeSettingsV2));
            }
          } else {
            // Factory defaults (applyToBasicScreen overlays theme colors)
            if (isSkip) {
              onChange(config.path, getFactorySkipButtonDefaults(locale, defaultText || 'Skip'));
            } else if (isBack) {
              onChange(config.path, getFactoryBackButtonDefaults(locale, defaultText || '❮'));
            } else {
              onChange(config.path, getFactoryCloseButtonDefaults());
            }
          }
        } else {
          // Non-l10n fields (e.g. close button without l10nPath)
          if (themeSettingsV2 && config.path.includes('close')) {
            onChange(config.path, getThemedCloseButtonDefaults(themeSettingsV2));
          } else if (config.path.includes('close')) {
            onChange(config.path, getFactoryCloseButtonDefaults());
          } else {
            onChange(config.path, config.defaultValue ?? {});
          }
        }
      } else {
        onChange(config.path, null);
      }
    },
    [onChange, locale, themeSettingsV2]
  );

  /** Write to a progressIndicator sub-path, normalizing raw-number data first */
  const safeIndicatorChange = useCallback(
    (fullPath: string, value: unknown) => {
      const indicatorPath = V2_PROGRESS_INDICATOR_CONFIG.path;
      if (!fullPath.startsWith(`${indicatorPath}.`)) {
        onChange(fullPath, value);
        return;
      }
      const subPath = fullPath.slice(indicatorPath.length + 1);
      const raw = getValueByPath<unknown>(step, indicatorPath);

      if (typeof raw === 'number' || (raw && typeof raw === 'object' && !('value' in (raw as Record<string, unknown>)))) {
        // Build normalized object, apply sub-path change, write as single update
        const base: Record<string, unknown> = typeof raw === 'number'
          ? { value: raw, kind: 'linearBar' }
          : { ...(raw as Record<string, unknown>), value: V2_PROGRESS_INDICATOR_CONFIG.defaultValue ?? 50 };
        setNestedValue(base, subPath, value);
        onChange(indicatorPath, base);
      } else {
        onChange(fullPath, value);
      }
    },
    [onChange, step]
  );

  // Handle style field change — routes indicator paths through normalization
  const handleStyleChange = useCallback(
    (stylePath: string, value: unknown) => safeIndicatorChange(stylePath, value),
    [safeIndicatorChange]
  );

  // Memoize Back Button props
  const backButtonProps = useMemo(() => ({
    fieldConfig: V2_BACK_BUTTON_CONFIG,
    value: getFieldValue(V2_BACK_BUTTON_CONFIG),
    templateValue: undefined,
    enabled: checkFieldEnabled(V2_BACK_BUTTON_CONFIG),
    locale,
    styleValues: getStyleValues(V2_BACK_BUTTON_CONFIG),
    expandedStyles: expandedFields.has(V2_BACK_BUTTON_CONFIG.path),
  }), [getFieldValue, checkFieldEnabled, locale, getStyleValues, expandedFields]);

  // Memoize Skip Button props
  const skipButtonProps = useMemo(() => ({
    fieldConfig: V2_SKIP_BUTTON_CONFIG,
    value: getFieldValue(V2_SKIP_BUTTON_CONFIG),
    templateValue: undefined,
    enabled: checkFieldEnabled(V2_SKIP_BUTTON_CONFIG),
    locale,
    styleValues: getStyleValues(V2_SKIP_BUTTON_CONFIG),
    expandedStyles: expandedFields.has(V2_SKIP_BUTTON_CONFIG.path),
  }), [getFieldValue, checkFieldEnabled, locale, getStyleValues, expandedFields]);

  // ── Page Indicator handlers (flat ProgressIndicator) ─────────────

  const getIndicatorKind = useCallback((): V2PageIndicatorKindType => {
    const kind = getValueByPath<string>(step, 'content.header.progressIndicator.kind');
    if (kind === 'segmented' || kind === 'segmentedTitle') return kind;
    return 'linearBar';
  }, [step]);

  const getNumericFieldValue = useCallback(
    (config: FieldConfig): number => {
      const valuePath = config.valuePath ?? config.path;
      const rawValue = getValueByPath<unknown>(step, valuePath);
      if (typeof rawValue === 'number') return rawValue;
      if (typeof rawValue === 'object' && rawValue !== null && 'value' in rawValue) {
        const objValue = (rawValue as { value: unknown }).value;
        if (typeof objValue === 'number') return objValue;
      }
      return (config.defaultValue as number) ?? 0;
    },
    [step]
  );

  const handleNumericFieldChange = useCallback(
    (config: FieldConfig, value: number) => {
      const valuePath = config.valuePath ?? config.path;
      const currentValue = getValueByPath<unknown>(step, valuePath);
      if (typeof currentValue === 'object' && currentValue !== null) {
        // Always write to .value sub-path for objects (even if 'value' doesn't exist yet)
        onChange(`${valuePath}.value`, value);
      } else {
        onChange(valuePath, value);
      }
    },
    [onChange, step]
  );

  const handleIndicatorKindChange = useCallback(
    (kind: V2PageIndicatorKindType) => {
      const indicatorPath = V2_PROGRESS_INDICATOR_CONFIG.path;
      const raw = getValueByPath<unknown>(step, indicatorPath);

      // Build normalized base — preserves value from raw number or existing object
      let indicator: Record<string, unknown>;
      if (typeof raw === 'number') {
        indicator = { value: raw, kind: 'linearBar' };
      } else if (raw && typeof raw === 'object') {
        indicator = { ...(raw as Record<string, unknown>) };
        if (!('value' in indicator)) indicator.value = 50;
      } else {
        indicator = { value: 50, kind: 'linearBar' };
      }

      indicator.kind = kind;

      // Apply dashes defaults (v2.13.0: flat fields)
      if (kind === 'segmented' || kind === 'segmentedTitle') {
        if (indicator.activeColor == null) indicator.activeColor = '#12777f';
        if (indicator.trackColor == null) indicator.trackColor = '#f2f2f2';
        if (indicator.dashHeight == null) indicator.dashHeight = 5;
        if (indicator.dashesSpacing == null) indicator.dashesSpacing = 7;
        if (!indicator.numberOfDashes) indicator.numberOfDashes = 5;

        if (kind === 'segmentedTitle') {
          const existingTitle = indicator.title as Record<string, unknown> | undefined;
          if (!existingTitle) {
            const titleDefault = typeof V2_PROGRESS_INDICATOR_CONFIG.titleConfig?.defaultValue === 'string'
              ? V2_PROGRESS_INDICATOR_CONFIG.titleConfig.defaultValue : '5/10';
            indicator.title = {
              translations: { [locale]: titleDefault },
              appearance: {
                textAlign: 'start', fontSize: 15,
                fontWeight: 400, color: '#eee7e7',
              },
            };
          }
        }
      }

      // Single atomic write — avoids stale-closure issue with multiple sequential calls
      onChange(indicatorPath, indicator);
    },
    [onChange, step, locale]
  );

  // Dashes config (flat paths)
  const getDashesConfig = useCallback(() => {
    const pi = getValueByPath<Record<string, unknown>>(step, 'content.header.progressIndicator') || {};
    return {
      numberOfDashes: typeof pi.numberOfDashes === 'number' ? pi.numberOfDashes : 5,
      dashHeight: typeof pi.dashHeight === 'number' ? pi.dashHeight : 5,
      dashSpacing: typeof pi.dashesSpacing === 'number' ? pi.dashesSpacing : 7,
    };
  }, [step]);

  const handleDashesConfigChange = useCallback(
    (field: string, value: number) => {
      const fieldMap: Record<string, string> = {
        numberOfDashes: `${V2_PROGRESS_INDICATOR_CONFIG.path}.numberOfDashes`,
        dashHeight: `${V2_PROGRESS_INDICATOR_CONFIG.path}.dashHeight`,
        dashesSpacing: `${V2_PROGRESS_INDICATOR_CONFIG.path}.dashesSpacing`,
      };
      const path = fieldMap[field];
      if (path) safeIndicatorChange(path, value);
    },
    [safeIndicatorChange]
  );

  const getDashesStyleValues = useCallback((): Record<string, unknown> => {
    return getStyleValuesUtil(step as unknown as Record<string, unknown>, V2_PROGRESS_INDICATOR_CONFIG);
  }, [step]);

  // Title handlers (universal, for any kind)
  const getTitleValue = useCallback((): string => {
    if (!V2_PROGRESS_INDICATOR_CONFIG.titleConfig) return '';
    const tc = V2_PROGRESS_INDICATOR_CONFIG.titleConfig;
    if (tc.l10nPath) return getV2TranslationValue(step, tc.l10nPath, locale);
    return getValueByPath<string>(step, tc.path) ?? '';
  }, [step, locale]);

  const isTitleEnabled = useCallback((): boolean => {
    if (!V2_PROGRESS_INDICATOR_CONFIG.titleConfig) return false;
    return isFieldEnabled(step, V2_PROGRESS_INDICATOR_CONFIG.titleConfig.path);
  }, [step]);

  const getTitleStyleValues = useCallback((): Record<string, unknown> => {
    if (!V2_PROGRESS_INDICATOR_CONFIG.titleConfig) return {};
    return getStyleValuesUtil(step as unknown as Record<string, unknown>, V2_PROGRESS_INDICATOR_CONFIG.titleConfig);
  }, [step]);

  const handleTitleChange = useCallback((value: string) => {
    if (!V2_PROGRESS_INDICATOR_CONFIG.titleConfig) return;
    const tc = V2_PROGRESS_INDICATOR_CONFIG.titleConfig;
    if (tc.l10nPath) {
      safeIndicatorChange(`${tc.l10nPath}.translations.${locale}`, value);
    } else {
      safeIndicatorChange(tc.path, value);
    }
  }, [safeIndicatorChange, locale]);

  const handleTitleEnabledChange = useCallback((enabled: boolean) => {
    if (!V2_PROGRESS_INDICATOR_CONFIG.titleConfig) return;
    const tc = V2_PROGRESS_INDICATOR_CONFIG.titleConfig;
    if (enabled) {
      // Title is an l10n text field — must create proper structure with translations,
      // not a plain string. Otherwise getV2TranslationValue can't read it and
      // handleTitleChange can't deep-set into title.translations[locale].
      const defaultText = typeof tc.defaultValue === 'string' ? tc.defaultValue : '5/10';
      safeIndicatorChange(tc.path, {
        translations: { [locale]: defaultText },
        appearance: {},
      });
    } else {
      safeIndicatorChange(tc.path, null);
    }
  }, [safeIndicatorChange, locale]);

  // Memoize page indicator props
  const pageIndicatorProps = useMemo(() => {
    const dashesConfig = getDashesConfig();
    return {
      fieldConfig: V2_PROGRESS_INDICATOR_CONFIG,
      indicatorKind: getIndicatorKind(),
      lineValue: getNumericFieldValue(V2_PROGRESS_INDICATOR_CONFIG),
      enabled: checkFieldEnabled(V2_PROGRESS_INDICATOR_CONFIG),
      lineStyleValues: getStyleValues(V2_PROGRESS_INDICATOR_CONFIG),
      expandedStyles: expandedFields.has(V2_PROGRESS_INDICATOR_CONFIG.path),
      dashesNumber: dashesConfig.numberOfDashes,
      dashHeight: dashesConfig.dashHeight,
      dashSpacing: dashesConfig.dashSpacing,
      dashesStyleValues: getDashesStyleValues(),
      titleValue: getTitleValue(),
      titleEnabled: isTitleEnabled(),
      titleStyleValues: getTitleStyleValues(),
      titleStylesExpanded: expandedFields.has(V2_PROGRESS_INDICATOR_CONFIG.titleConfig?.path || ''),
      locale,
    };
  }, [
    getIndicatorKind, getNumericFieldValue, checkFieldEnabled, getStyleValues, expandedFields,
    getDashesConfig, getDashesStyleValues, getTitleValue, isTitleEnabled, getTitleStyleValues, locale
  ]);

  // ── Header Settings handlers ────────────────────────────────────────

  const handleHeaderPaddingChange = useCallback(
    (path: string, value: number) => {
      const translated = translatePaddingPath(path);
      onChange(translated, value);
    },
    [onChange]
  );

  const headerInsetValues = useMemo(
    () => getEdgeInsetsAsPaddingValues(step, V2_HEADER_INSETS_PATH),
    [step]
  );

  // ── Icon handlers (reuses button icon pattern via ButtonStyleSections) ──

  const uploadService = useMemo(() => new OnboardingMediaUploadService(), []);

  const handleIconUpload = useCallback(
    async (file: File): Promise<string> => {
      const result = await uploadService.uploadImage(file, {
        projectId: projectId ?? '',
        onboardingId: onboardingId ?? '',
        versionId: versionId ?? '',
        locale,
      });
      return result.assetUrl.origin;
    },
    [uploadService, projectId, onboardingId, versionId, locale]
  );

  // Build flat values map for icon ButtonStyleSections
  const iconStyleValues = useMemo((): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    // Extract values for each icon field
    for (const field of V2_PROGRESS_ICON_FIELDS) {
      result[field.path] = getValueByPath(step, field.path) ?? undefined;
    }
    // Extract icon insets for PaddingElement
    const insetsPath = V2_PROGRESS_ICON_STYLE_GROUP.paddingConfig!.basePath;
    const insets = getValueByPath<Record<string, unknown>>(step, insetsPath);
    if (insets && typeof insets === 'object') {
      result[`${insetsPath}.top`] = insets.top;
      result[`${insetsPath}.end`] = insets.end;
      result[`${insetsPath}.bottom`] = insets.bottom;
      result[`${insetsPath}.start`] = insets.start;
    }
    return result;
  }, [step]);

  // ── Progress Layout handlers ──────────────────────────────────────

  const progressFullWidth = useMemo(
    () => getValueByPath<boolean>(step, V2_PROGRESS_FULL_WIDTH_CONFIG.path) ?? false,
    [step]
  );

  const progressRespectButton = useMemo(
    () => getValueByPath<boolean>(step, V2_PROGRESS_RESPECT_BUTTON_CONFIG.path) ?? true,
    [step]
  );

  const progressVAlign = useMemo(
    () => getValueByPath<string>(step, V2_PROGRESS_V_ALIGN_CONFIG.path) ?? 'center',
    [step]
  );

  const progressInsetValues = useMemo(
    () => getEdgeInsetsAsPaddingValues(step, V2_PROGRESS_INSETS_PATH),
    [step]
  );

  const handleProgressPaddingChange = useCallback(
    (path: string, value: number) => {
      const translated = translatePaddingPath(path);
      onChange(translated, value);
    },
    [onChange]
  );

  const SECTION_NAME = 'Header';

  // ── Children injected into V2PageIndicatorElement's gear Collapse ──

  const indicatorChildren = useMemo(() => (
    <>
      {/* Icon sub-section (reuses button icon accordion) */}
      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <ButtonStyleSections
          sections={[V2_PROGRESS_ICON_STYLE_GROUP]}
          values={iconStyleValues}
          onChange={onChange}
          disabled={disabled}
          locale={locale}
          onIconUpload={handleIconUpload}
        />
      </Box>

      {/* Content Alignment (icon+title container alignment, NOT part of icon) */}
      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <StyleFieldRow
          config={V2_PROGRESS_CONTENT_ALIGNMENT_CONFIG}
          value={getValueByPath(step, V2_PROGRESS_CONTENT_ALIGNMENT_CONFIG.path) ?? 'center'}
          onChange={(value) => onChange(V2_PROGRESS_CONTENT_ALIGNMENT_CONFIG.path, value)}
          disabled={disabled}
        />
      </Box>

      {/* Progress Layout sub-section */}
      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Progress Layout</Typography>
        <Box data-testid="row-header-progress-full-width" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">Full Width</Typography>
          <Switch
            size="small"
            checked={progressFullWidth}
            onChange={(e) => onChange(V2_PROGRESS_FULL_WIDTH_CONFIG.path, e.target.checked)}
            disabled={disabled}
            data-testid="toggle-header-progress-full-width"
          />
        </Box>
        <Box data-testid="row-header-progress-respect-buttons" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">Respect Buttons</Typography>
          <Switch
            size="small"
            checked={progressRespectButton}
            onChange={(e) => onChange(V2_PROGRESS_RESPECT_BUTTON_CONFIG.path, e.target.checked)}
            disabled={disabled}
            data-testid="toggle-header-progress-respect-buttons"
          />
        </Box>
        <Box data-testid="row-header-progress-vertical-align" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">Vertical Align</Typography>
          <FormControl size="small" variant="standard" sx={{ minWidth: 100 }}>
            <Select
              value={progressVAlign}
              onChange={(e) => onChange(V2_PROGRESS_V_ALIGN_CONFIG.path, e.target.value)}
              disabled={disabled}
              displayEmpty
              data-testid="select-header-progress-vertical-align"
            >
              {V2_PROGRESS_V_ALIGN_CONFIG.options?.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <PaddingElement
          basePath={V2_PROGRESS_INSETS_PATH}
          values={progressInsetValues}
          onChange={handleProgressPaddingChange}
          label="Progress Padding"
          disabled={disabled}
        />
      </Box>
    </>
  ), [
    iconStyleValues, disabled, locale, step, onChange, handleIconUpload,
    progressFullWidth, progressRespectButton, progressVAlign, progressInsetValues, handleProgressPaddingChange,
  ]);

  return (
    <SAccordion
      title={sectionTitle ?? SECTION_NAME}
      collapsible
      defaultExpanded={defaultExpanded}
      sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
      data-testid={getSectionTestId(SECTION_NAME)}
    >
      <EditorGuideProvider>
        {/* Back Button */}
        <TextElement
          {...backButtonProps}
          onChange={(value) => handleFieldChange(V2_BACK_BUTTON_CONFIG, value)}
          onEnabledChange={(enabled) => handleEnabledChange(V2_BACK_BUTTON_CONFIG, enabled)}
          onStylesExpandToggle={() => toggleFieldExpansion(V2_BACK_BUTTON_CONFIG.path)}
          onStyleChange={handleStyleChange}
          disabled={disabled}
          data-testid={getFieldTestId(SECTION_NAME, V2_BACK_BUTTON_CONFIG.label)}
          checkboxTestId={getCheckboxTestId(SECTION_NAME, V2_BACK_BUTTON_CONFIG.label)}
          stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, V2_BACK_BUTTON_CONFIG.label)}
          styleGroupTestId={getStyleGroupTestId(SECTION_NAME, V2_BACK_BUTTON_CONFIG.label)}
          inputTestId={getInputTestId(SECTION_NAME, V2_BACK_BUTTON_CONFIG.label)}
        />

        {/* Skip Button */}
        <TextElement
          {...skipButtonProps}
          onChange={(value) => handleFieldChange(V2_SKIP_BUTTON_CONFIG, value)}
          onEnabledChange={(enabled) => handleEnabledChange(V2_SKIP_BUTTON_CONFIG, enabled)}
          onStylesExpandToggle={() => toggleFieldExpansion(V2_SKIP_BUTTON_CONFIG.path)}
          onStyleChange={handleStyleChange}
          disabled={disabled}
          data-testid={getFieldTestId(SECTION_NAME, V2_SKIP_BUTTON_CONFIG.label)}
          checkboxTestId={getCheckboxTestId(SECTION_NAME, V2_SKIP_BUTTON_CONFIG.label)}
          stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, V2_SKIP_BUTTON_CONFIG.label)}
          styleGroupTestId={getStyleGroupTestId(SECTION_NAME, V2_SKIP_BUTTON_CONFIG.label)}
          inputTestId={getInputTestId(SECTION_NAME, V2_SKIP_BUTTON_CONFIG.label)}
        />

        {/* Page Indicator (with Logo & Progress Layout as children inside gear Collapse) */}
        <V2PageIndicatorElement
          fieldConfig={V2_PROGRESS_INDICATOR_CONFIG}
          indicatorKind={pageIndicatorProps.indicatorKind}
          lineValue={pageIndicatorProps.lineValue}
          enabled={pageIndicatorProps.enabled}
          onKindChange={handleIndicatorKindChange}
          onLineValueChange={(value) => handleNumericFieldChange(V2_PROGRESS_INDICATOR_CONFIG, value)}
          onEnabledChange={(enabled) => handleEnabledChange(V2_PROGRESS_INDICATOR_CONFIG, enabled)}
          disabled={disabled}
          expandedStyles={pageIndicatorProps.expandedStyles}
          onStylesExpandToggle={() => toggleFieldExpansion(V2_PROGRESS_INDICATOR_CONFIG.path)}
          lineStyleValues={pageIndicatorProps.lineStyleValues}
          onLineStyleChange={handleStyleChange}
          dashesNumber={pageIndicatorProps.dashesNumber}
          dashHeight={pageIndicatorProps.dashHeight}
          dashSpacing={pageIndicatorProps.dashSpacing}
          onDashesConfigChange={handleDashesConfigChange}
          dashesStyleValues={pageIndicatorProps.dashesStyleValues}
          onDashesStyleChange={handleStyleChange}
          titleValue={pageIndicatorProps.titleValue}
          titleEnabled={pageIndicatorProps.titleEnabled}
          titleStyleValues={pageIndicatorProps.titleStyleValues}
          onTitleChange={handleTitleChange}
          onTitleEnabledChange={handleTitleEnabledChange}
          onTitleStyleChange={handleStyleChange}
          titleStylesExpanded={pageIndicatorProps.titleStylesExpanded}
          onTitleStylesExpandToggle={() => toggleFieldExpansion(V2_PROGRESS_INDICATOR_CONFIG.titleConfig?.path || '')}
          locale={locale}
          data-testid={getFieldTestId(SECTION_NAME, V2_PROGRESS_INDICATOR_CONFIG.label)}
          checkboxTestId={getCheckboxTestId(SECTION_NAME, V2_PROGRESS_INDICATOR_CONFIG.label)}
          stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, V2_PROGRESS_INDICATOR_CONFIG.label)}
          inputTestId={getInputTestId(SECTION_NAME, V2_PROGRESS_INDICATOR_CONFIG.label)}
          sliderTestId={getSliderTestId(SECTION_NAME, V2_PROGRESS_INDICATOR_CONFIG.label)}
          kindSelectTestId="select-header-page-indicator-kind"
        >
          {indicatorChildren}
        </V2PageIndicatorElement>

        {/* ── Header Settings (label + gear icon + Collapse) ── */}
        <Box sx={{ mb: FIELD_MARGIN_BOTTOM }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={mainFieldLabelEnabledSx}>
              Header Settings
            </Typography>
            <IconButton
              onClick={() => setHeaderSettingsExpanded(prev => !prev)}
              size="small"
              disabled={disabled}
              aria-label="Toggle header settings"
              data-testid={getStylesToggleTestId(SECTION_NAME, 'Header Settings')}
              sx={headerSettingsExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
            >
              <TuneIcon fontSize="small" />
            </IconButton>
          </Box>
          <Collapse in={headerSettingsExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: FIELD_STYLE_MARGIN_TOP, ...expandedSectionSx }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: FIELD_STYLE_GRID_GAP }}>
                <StyleFieldRow
                  config={V2_HEADER_HEIGHT_CONFIG}
                  value={getValueByPath(step, V2_HEADER_HEIGHT_CONFIG.path) ?? V2_HEADER_HEIGHT_CONFIG.defaultValue}
                  onChange={(value) => onChange(V2_HEADER_HEIGHT_CONFIG.path, value)}
                  disabled={disabled}
                />
                <StyleFieldRow
                  config={V2_HEADER_BG_COLOR_CONFIG}
                  value={getValueByPath(step, V2_HEADER_BG_COLOR_CONFIG.path)}
                  onChange={(value) => onChange(
                    V2_HEADER_BG_COLOR_CONFIG.path,
                    value === '' || value === undefined ? null : value
                  )}
                  disabled={disabled}
                />
                <StyleFieldRow
                  config={V2_HEADER_OPACITY_CONFIG}
                  value={getValueByPath(step, V2_HEADER_OPACITY_CONFIG.path) ?? V2_HEADER_OPACITY_CONFIG.defaultValue}
                  onChange={(value) => onChange(V2_HEADER_OPACITY_CONFIG.path, value)}
                  disabled={disabled}
                />
              </Box>
              <Box sx={{ mt: 1 }}>
                <PaddingElement
                  basePath={V2_HEADER_INSETS_PATH}
                  values={headerInsetValues}
                  onChange={handleHeaderPaddingChange}
                  label="Padding"
                  disabled={disabled}
                />
              </Box>
            </Box>
          </Collapse>
        </Box>
      </EditorGuideProvider>
    </SAccordion>
  );
};
